<template>
  <div class="design-elements">
    <div class="design-elements__header">
      <div>
        <h1 class="design-elements__title">{{ $t("design_elements.title") }}</h1>
        <p class="design-elements__subtitle">{{ $t("design_elements.subtitle") }}</p>
      </div>
      <v-btn color="primary" outlined height="38" @click="exportLibrary">
        <v-icon size="18" class="mr-2">mdi-download-outline</v-icon>
        {{ $t("design_elements.export_library") }}
      </v-btn>
    </div>

    <div class="design-elements__body">
      <aside class="design-elements__categories">
        <button
          type="button"
          class="design-elements__category"
          :class="{ 'design-elements__category--active': activeCategory === null }"
          @click="activeCategory = null">
          <span>{{ $t("design_elements.all_categories") }}</span>
          <span class="design-elements__category-count">{{ elements.length }}</span>
        </button>
        <button
          v-for="category in categories"
          :key="category.id"
          type="button"
          class="design-elements__category"
          :class="{ 'design-elements__category--active': activeCategory === category.id }"
          @click="activeCategory = category.id">
          <span>{{ category.label }}</span>
          <span class="design-elements__category-count">{{ category.count }}</span>
        </button>
      </aside>

      <section class="design-elements__list">
        <v-text-field
          class="search-filter design-elements__search"
          hide-details
          clearable
          v-model="search"
          :placeholder="$t('design_elements.search_placeholder')"></v-text-field>

        <p class="design-elements__result-count">
          {{ $tc("design_elements.result_count", filteredElements.length, { count: filteredElements.length }) }}
        </p>

        <p v-if="!filteredElements.length" class="design-elements__no-results">
          {{ $t("design_elements.no_results") }}
        </p>

        <article v-for="element in filteredElements" :key="element.name" class="element-card">
          <div class="element-card__header">
            <div class="element-card__heading">
              <h2 class="element-card__name">{{ element.name }}</h2>
              <span class="element-card__category">{{ element.categoryLabel }}</span>
              <template v-if="element.staticPreview && !showsExample(element)">
                <span class="element-card__category">{{ $t("design_elements.static_preview") }}</span>
                <InfoTooltip :info="$t('design_elements.static_preview_hint')" />
              </template>
            </div>
            <v-btn icon small @click="toggleDetails(element.name)">
              <v-icon>{{ isExpanded(element.name) ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
            </v-btn>
          </div>

          <p class="element-card__description">{{ element.description }}</p>

          <DesignElementPreview
            :html="showsExample(element) ? serializedExample(element) : element.markup"
            :title="element.name"
            :mobile="isMobile(element.name)"
            :example="showsExample(element)"
            :runtime-scripts="runtimeScripts"
            :runtime-styles="runtimeStyles" />

          <v-btn-toggle
            class="element-card__viewport-toggle"
            :value="isMobile(element.name) ? 1 : 0"
            mandatory
            dense
            @change="setViewport(element, $event)">
            <v-btn small>{{ $t("design_elements.desktop") }}</v-btn>
            <v-btn small>{{ $t("design_elements.mobile") }}</v-btn>
          </v-btn-toggle>

          <div v-if="isExpanded(element.name)" class="element-card__details">
            <div class="element-card__tags">
              <span v-for="tag in element.tags" :key="tag" class="element-card__tag">{{ tag }}</span>
            </div>

            <div class="element-card__field">
              <div class="element-card__field-label">{{ $t("design_elements.controls_title") }}</div>
              <p v-if="!controlList(element).length" class="element-card__note">
                {{ $t("design_elements.no_controls") }}
              </p>
              <template v-else>
                <p v-if="element.configAttribute" class="element-card__note">
                  {{ $t("design_elements.config_stored_in", { attribute: element.configAttribute }) }}
                  <code>{{ element.configEncoding }}</code>
                </p>
                <div class="element-card__controls">
                  <div v-for="[name, type] in controlList(element)" :key="name" class="element-card__control">
                    <span class="element-card__control-name">{{ name }}</span>
                    <span class="element-card__control-type">{{ type }}</span>
                  </div>
                </div>
              </template>
            </div>

            <div class="element-card__field">
              <div class="element-card__field-header">
                <div class="element-card__field-label">{{ $t("design_elements.snippet_title") }}</div>
                <CopyText :value="element.snippet" display-icon :text-disclaimer="$t('design_elements.copy_code')" />
              </div>
              <textarea class="element-card__code" readonly rows="6" :value="element.snippet" @focus="selectAll" />
            </div>

            <div class="element-card__field">
              <div class="element-card__field-header">
                <div class="element-card__field-label">{{ $t("design_elements.spec_title") }}</div>
                <CopyText
                  :value="elementSpec(element)"
                  display-icon
                  :text-disclaimer="$t('design_elements.copy_for_ai')" />
              </div>
              <textarea class="element-card__code" readonly rows="8" :value="elementSpec(element)" @focus="selectAll" />
            </div>
          </div>
        </article>
      </section>
    </div>
  </div>
</template>

<script>
  import CopyText from "@/components/custom/CopyText.vue";
  import DesignElementPreview from "@/components/custom/DesignElementPreview.vue";
  import InfoTooltip from "@/components/custom/InfoTooltip.vue";
  import { serializeDynamicComponents } from "@/assets/utilities/tinymce/visualization";
  import { getCategoriesWithCounts, getDesignElements } from "@/assets/utilities/tinymce/shortcodes/elementsCatalog";
  import { parseToHtml } from "@/utility/parseToHtml";

  const EXPORT_FILE_NAME = "design-elements.json";

  const NO_RUNTIME_ASSETS = { scripts: [], styles: [] };

  /**
   * The page's own component scripts and stylesheets, listed by the API because /build carries no
   * CORS headers. The endpoint sits behind the API firewall, so the request has to carry the
   * session cookie. Without them the library simply keeps showing placeholders.
   */
  const fetchElementRuntimeAssets = async () => {
    try {
      const response = await fetch(`${process.env.VUE_APP_API_URL}/js/elements`, { credentials: "include" });

      if (!response.ok) return NO_RUNTIME_ASSETS;

      const { js, css } = await response.json();
      const onApiOrigin = paths => (paths ?? []).map(path => `${process.env.VUE_APP_API}${path}`);

      return { scripts: onApiOrigin(js), styles: onApiOrigin(css) };
    } catch {
      // An API without the endpoint yet just means the library keeps showing placeholders.
      return NO_RUNTIME_ASSETS;
    }
  };

  export default {
    name: "DesignElementsLibrary",
    components: { CopyText, DesignElementPreview, InfoTooltip },
    data() {
      const elements = getDesignElements();

      return {
        elements,
        categories: getCategoriesWithCounts(elements),
        activeCategory: null,
        search: "",
        expanded: [],
        mobileViewports: [],
        runtimeScripts: [],
        runtimeStyles: [],
      };
    },
    async created() {
      const { scripts, styles } = await fetchElementRuntimeAssets();

      this.runtimeScripts = scripts;
      this.runtimeStyles = styles;
    },
    computed: {
      filteredElements() {
        const query = (this.search || "").trim().toLowerCase();

        return this.elements.filter(element => {
          if (this.activeCategory && element.category !== this.activeCategory) return false;
          if (!query) return true;

          return [element.name, element.categoryLabel, element.description, ...element.tags]
            .join(" ")
            .toLowerCase()
            .includes(query);
        });
      },
    },
    methods: {
      isExpanded(name) {
        return this.expanded.includes(name);
      },
      toggleDetails(name) {
        this.expanded = this.isExpanded(name)
          ? this.expanded.filter(expandedName => expandedName !== name)
          : [...this.expanded, name];
      },
      isMobile(name) {
        return this.mobileViewports.includes(name);
      },
      controlList(element) {
        return Object.entries(element.controls);
      },
      // Only elements whose editor markup is a placeholder need the page runtime; the rest already
      // render their real markup.
      showsExample(element) {
        return element.staticPreview && Boolean(element.example) && this.runtimeScripts.length > 0;
      },
      /**
       * The same conversion a page save runs, so the frame receives the component tag the live
       * page would carry rather than the editor's placeholder.
       */
      serializedExample(element) {
        const parsed = parseToHtml(element.example);
        serializeDynamicComponents(parsed);

        return parsed.body.innerHTML;
      },
      // The dashboard is served over plain http on some environments, where the clipboard API is
      // unavailable, so the fields double as the manual copy path. Selecting on the next tick,
      // because the browser places its own caret after the focus handler runs.
      selectAll(event) {
        this.$nextTick(() => event.target.select());
      },
      setViewport(element, toggleIndex) {
        this.mobileViewports =
          toggleIndex === 1
            ? [...this.mobileViewports, element.name]
            : this.mobileViewports.filter(name => name !== element.name);
      },
      /**
       * The spec is what a user hands to an AI assistant, so it carries the same fields the
       * library shows plus the markup the assistant should reproduce.
       */
      /**
       * The spec has to be enough on its own for an assistant to emit a finished element, so it
       * carries a configured snippet rather than an empty shell, the config that snippet holds
       * decoded to its real shape, and how to write a changed config back into the markup.
       */
      elementSpec(element) {
        // Three states, and they have to be told apart: a sample config to adapt, a config
        // attribute with no sample to show, or no configuration at all. Keying this off the
        // example alone told several elements to edit an `exampleConfig` the spec never carried.
        const usageKey = element.exampleConfig
          ? "usage_configured"
          : element.configAttribute
          ? "usage_config_no_sample"
          : "usage_plain";

        return JSON.stringify(
          {
            name: element.name,
            category: element.categoryLabel,
            description: element.description,
            tags: element.tags,
            controls: element.controls,
            ...(element.configAttribute
              ? { configAttribute: element.configAttribute, configEncoding: element.configEncoding }
              : {}),
            ...(element.nodeTypes ? { nodeTypes: element.nodeTypes } : {}),
            ...(element.nestedEncoding ? { nestedEncoding: element.nestedEncoding } : {}),
            ...(element.exampleConfig ? { exampleConfig: element.exampleConfig } : {}),
            usage:
              this.$t(`design_elements.${usageKey}`) +
              (element.nestedEncoding ? ` ${this.$t("design_elements.usage_nested")}` : ""),
            snippet: element.example || element.snippet,
          },
          null,
          2,
        );
      },
      exportLibrary() {
        const library = this.elements.map(element => JSON.parse(this.elementSpec(element)));
        const blobUrl = URL.createObjectURL(new Blob([JSON.stringify(library, null, 2)], { type: "application/json" }));

        try {
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = EXPORT_FILE_NAME;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } finally {
          URL.revokeObjectURL(blobUrl);
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/functions.scss";
  @import "@core/preset/preset/typography/index.scss";

  .design-elements {
    &__header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 24px;
      margin-bottom: 24px;
    }

    &__title {
      @include heading-3();
      color: get-color("headline");
    }

    &__subtitle {
      @include text-body-2();
      color: get-color("subtitle");
      margin: 4px 0 0;
      max-width: 640px;
    }

    &__body {
      display: flex;
      align-items: flex-start;
      gap: 24px;
    }

    &__categories {
      position: sticky;
      top: 24px;
      flex: 0 0 232px;
      display: flex;
      flex-direction: column;
      gap: 2px;
      padding: 8px;
      background-color: get-color("white");
      border: 1px solid get-color("border");
      border-radius: 8px;
    }

    &__category {
      @include text-body-2();
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 8px 12px;
      border-radius: 6px;
      color: get-color("body");
      text-align: left;
      cursor: pointer;

      &:hover {
        background-color: get-color("hover");
      }

      &--active {
        background-color: get-color("primary-10-hover");
        color: get-color("primary");
      }
    }

    &__category-count {
      @include text-caption();
      color: get-color("subtitle");
    }

    &__list {
      flex: 1 1 auto;
      min-width: 0;
    }

    &__search {
      margin-bottom: 8px;
    }

    &__result-count {
      @include text-caption();
      color: get-color("subtitle");
      margin-bottom: 16px;
    }

    &__no-results {
      @include text-body-2();
      color: get-color("subtitle");
      padding: 32px 0;
      text-align: center;
    }
  }

  .element-card {
    background-color: get-color("white");
    border: 1px solid get-color("border");
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
    }

    &__heading {
      display: flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
    }

    &__name {
      @include text-body-1-bold();
      color: get-color("headline");
    }

    &__category {
      @include text-caption();
      color: get-color("subtitle");
      background-color: get-color("element-bg-200");
      border-radius: 4px;
      padding: 2px 8px;
      white-space: nowrap;
    }

    &__actions {
      display: flex;
      align-items: center;
      gap: 4px;
      flex-shrink: 0;
    }

    &__description {
      @include text-body-2();
      color: get-color("subtitle");
      margin: 4px 0 12px;
    }

    &__details {
      margin-top: 16px;
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    &__viewport-toggle {
      margin-top: 12px;
    }

    &__tags {
      display: flex;
      gap: 6px;
      flex-wrap: wrap;
    }

    &__tag {
      @include text-caption();
      color: get-color("subtitle");
      background-color: get-color("element-bg-100");
      border-radius: 4px;
      padding: 2px 8px;
    }

    &__controls {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: 4px 16px;
    }

    &__control {
      @include text-caption();
      display: flex;
      align-items: baseline;
      gap: 8px;
      justify-content: space-between;
      border-bottom: 1px solid get-color("border");
      padding: 3px 0;
    }

    &__control-name {
      font-family: monospace;
      color: get-color("headline");
    }

    &__control-type {
      color: get-color("subtitle");
      text-align: right;
    }

    &__note {
      @include text-caption();
      color: get-color("subtitle");
      margin-bottom: 8px;

      code {
        font-family: monospace;
        background-color: get-color("element-bg-100");
      }
    }

    &__field-header {
      display: flex;
      align-items: center;
      margin-bottom: 4px;
    }

    &__field-label {
      @include text-caption-bold();
      color: get-color("headline");
    }

    &__code {
      @include text-caption();
      width: 100%;
      color: get-color("body");
      background-color: get-color("element-bg-100");
      border: 1px solid get-color("border");
      border-radius: 6px;
      padding: 12px;
      font-family: monospace;
      white-space: pre;
      resize: vertical;
    }
  }
</style>
